/**
 * Comprehensive Data Validation and Sanitization Utilities
 * Provides validation for scenario data, CTI inputs, and user interactions
 */

/**
 * Validation result structure
 */
export class ValidationResult {
  constructor(isValid = true, errors = [], warnings = []) {
    this.isValid = isValid;
    this.errors = errors;
    this.warnings = warnings;
  }

  addError(field, message) {
    this.errors.push({ field, message });
    this.isValid = false;
  }

  addWarning(field, message) {
    this.warnings.push({ field, message });
  }

  hasErrors() {
    return this.errors.length > 0;
  }

  hasWarnings() {
    return this.warnings.length > 0;
  }

  getErrorsForField(field) {
    return this.errors.filter(error => error.field === field);
  }

  getWarningsForField(field) {
    return this.warnings.filter(warning => warning.field === field);
  }
}

/**
 * Sanitize string input
 */
export const sanitizeString = (input, options = {}) => {
  const {
    maxLength = 1000,
    allowHtml = false,
    trimWhitespace = true,
    removeSpecialChars = false
  } = options;

  if (typeof input !== 'string') {
    return '';
  }

  let sanitized = input;

  // Trim whitespace
  if (trimWhitespace) {
    sanitized = sanitized.trim();
  }

  // Remove HTML tags if not allowed
  if (!allowHtml) {
    sanitized = sanitized.replace(/<[^>]*>/g, '');
  }

  // Remove special characters if requested
  if (removeSpecialChars) {
    sanitized = sanitized.replace(/[^\w\s\-_.]/g, '');
  }

  // Truncate to max length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized;
};

/**
 * Validate scenario data structure
 */
export const validateScenario = (scenario) => {
  const result = new ValidationResult();

  if (!scenario) {
    result.addError('scenario', 'Scénario requis');
    return result;
  }

  // Validate basic properties
  if (!scenario.name || typeof scenario.name !== 'string') {
    result.addError('name', 'Nom du scénario requis');
  } else if (scenario.name.length < 3) {
    result.addError('name', 'Le nom doit contenir au moins 3 caractères');
  } else if (scenario.name.length > 200) {
    result.addError('name', 'Le nom ne peut pas dépasser 200 caractères');
  }

  if (!scenario.description || typeof scenario.description !== 'string') {
    result.addError('description', 'Description du scénario requise');
  } else if (scenario.description.length < 10) {
    result.addError('description', 'La description doit contenir au moins 10 caractères');
  } else if (scenario.description.length > 2000) {
    result.addError('description', 'La description ne peut pas dépasser 2000 caractères');
  }

  // Validate operational steps
  if (!scenario.operationalSteps || !Array.isArray(scenario.operationalSteps)) {
    result.addError('operationalSteps', 'Étapes opérationnelles requises');
  } else {
    if (scenario.operationalSteps.length === 0) {
      result.addError('operationalSteps', 'Au moins une étape opérationnelle est requise');
    } else if (scenario.operationalSteps.length > 50) {
      result.addWarning('operationalSteps', 'Nombre très élevé d\'étapes opérationnelles (>50)');
    }

    // Validate each operational step
    scenario.operationalSteps.forEach((step, index) => {
      const stepResult = validateOperationalStep(step, index);
      result.errors.push(...stepResult.errors);
      result.warnings.push(...stepResult.warnings);
      if (!stepResult.isValid) {
        result.isValid = false;
      }
    });
  }

  // Validate probability if present
  if (scenario.probability !== undefined) {
    if (typeof scenario.probability !== 'number' || scenario.probability < 0 || scenario.probability > 100) {
      result.addError('probability', 'La probabilité doit être un nombre entre 0 et 100');
    }
  }

  // Validate severity if present
  if (scenario.severity !== undefined) {
    const validSeverities = ['Faible', 'Modérée', 'Élevée', 'Critique'];
    if (!validSeverities.includes(scenario.severity)) {
      result.addError('severity', `Sévérité invalide. Valeurs acceptées: ${validSeverities.join(', ')}`);
    }
  }

  return result;
};

/**
 * Validate operational step
 */
export const validateOperationalStep = (step, index = 0) => {
  const result = new ValidationResult();
  const fieldPrefix = `operationalSteps[${index}]`;

  if (!step) {
    result.addError(fieldPrefix, 'Étape opérationnelle requise');
    return result;
  }

  // Validate phase
  const validPhases = ['CONNAITRE', 'RENTRER', 'TROUVER', 'EXPLOITER'];
  if (!step.phase) {
    result.addError(`${fieldPrefix}.phase`, 'Phase EBIOS RM requise');
  } else if (!validPhases.includes(step.phase)) {
    result.addError(`${fieldPrefix}.phase`, `Phase invalide. Valeurs acceptées: ${validPhases.join(', ')}`);
  }

  // Validate technique
  if (!step.technique || typeof step.technique !== 'string') {
    result.addError(`${fieldPrefix}.technique`, 'Technique requise');
  } else if (step.technique.length < 3) {
    result.addError(`${fieldPrefix}.technique`, 'La technique doit contenir au moins 3 caractères');
  } else if (step.technique.length > 200) {
    result.addError(`${fieldPrefix}.technique`, 'La technique ne peut pas dépasser 200 caractères');
  }

  // Validate description
  if (!step.description || typeof step.description !== 'string') {
    result.addError(`${fieldPrefix}.description`, 'Description de l\'étape requise');
  } else if (step.description.length < 10) {
    result.addError(`${fieldPrefix}.description`, 'La description doit contenir au moins 10 caractères');
  } else if (step.description.length > 1000) {
    result.addError(`${fieldPrefix}.description`, 'La description ne peut pas dépasser 1000 caractères');
  }

  // Validate difficulty if present
  if (step.difficulty !== undefined) {
    const validDifficulties = ['Très faible', 'Faible', 'Moyen', 'Élevé', 'Très élevé'];
    if (!validDifficulties.includes(step.difficulty)) {
      result.addError(`${fieldPrefix}.difficulty`, `Difficulté invalide. Valeurs acceptées: ${validDifficulties.join(', ')}`);
    }
  }

  // Validate step probability if present
  if (step.stepProbability !== undefined) {
    if (typeof step.stepProbability !== 'number' || step.stepProbability < 0 || step.stepProbability > 100) {
      result.addError(`${fieldPrefix}.stepProbability`, 'La probabilité de l\'étape doit être un nombre entre 0 et 100');
    }
  }

  // Validate indicators array if present
  if (step.indicators !== undefined) {
    if (!Array.isArray(step.indicators)) {
      result.addError(`${fieldPrefix}.indicators`, 'Les indicateurs doivent être un tableau');
    } else if (step.indicators.length > 50) {
      result.addWarning(`${fieldPrefix}.indicators`, 'Nombre élevé d\'indicateurs (>50)');
    }
  }

  // Validate tools array if present
  if (step.tools !== undefined) {
    if (!Array.isArray(step.tools)) {
      result.addError(`${fieldPrefix}.tools`, 'Les outils doivent être un tableau');
    } else if (step.tools.length > 20) {
      result.addWarning(`${fieldPrefix}.tools`, 'Nombre élevé d\'outils (>20)');
    }
  }

  return result;
};

/**
 * Validate CTI data structure
 * Note: Attack paths without specific CVEs/techniques are still valid
 * as the AI can generate generic operational scenarios
 */
export const validateCTIData = (ctiData) => {
  const result = new ValidationResult();

  if (!ctiData) {
    result.addError('ctiData', 'Données CTI requises');
    return result;
  }

  // Validate attack paths array exists
  if (!ctiData.attackPathsWithCTI || !Array.isArray(ctiData.attackPathsWithCTI)) {
    result.addError('attackPathsWithCTI', 'Chemins d\'attaque avec CTI requis');
  } else if (ctiData.attackPathsWithCTI.length === 0) {
    // Note: This is a warning now, not an error - AI can still work with attack path context
    result.addWarning('attackPathsWithCTI', 'Aucun chemin d\'attaque trouvé - assurez-vous d\'avoir défini des chemins d\'attaque');
  }

  // Validate vulnerabilities format if present (but not required)
  if (ctiData.vulnerabilities !== undefined) {
    if (!Array.isArray(ctiData.vulnerabilities)) {
      result.addError('vulnerabilities', 'Les vulnérabilités doivent être un tableau');
    } else if (ctiData.vulnerabilities.length > 1000) {
      result.addWarning('vulnerabilities', 'Nombre très élevé de vulnérabilités (>1000)');
    }
  }

  // Validate techniques format if present (but not required)
  if (ctiData.techniques !== undefined) {
    if (!Array.isArray(ctiData.techniques)) {
      result.addError('techniques', 'Les techniques doivent être un tableau');
    } else if (ctiData.techniques.length > 500) {
      result.addWarning('techniques', 'Nombre très élevé de techniques (>500)');
    }
  }

  return result;
};

/**
 * Validate attack path selection
 */
export const validateAttackPathSelection = (selectedAssets, availableAssets) => {
  const result = new ValidationResult();

  if (!selectedAssets || !Array.isArray(selectedAssets)) {
    result.addError('selectedAssets', 'Sélection de chemins d\'attaque requise');
    return result;
  }

  if (selectedAssets.length === 0) {
    result.addError('selectedAssets', 'Au moins un chemin d\'attaque doit être sélectionné');
  } else if (selectedAssets.length > 1) {
    result.addError('selectedAssets', 'Un seul chemin d\'attaque peut être sélectionné à la fois');
  }

  // Validate that selected assets exist in available assets
  if (availableAssets && Array.isArray(availableAssets)) {
    const availableIds = availableAssets.map(asset => asset.id);
    const invalidSelections = selectedAssets.filter(id => !availableIds.includes(id));

    if (invalidSelections.length > 0) {
      result.addError('selectedAssets', `Chemins d'attaque invalides sélectionnés: ${invalidSelections.join(', ')}`);
    }
  }

  return result;
};

/**
 * Validate user input for manual scenario creation
 */
export const validateManualScenario = (manualScenario) => {
  const result = new ValidationResult();

  if (!manualScenario) {
    result.addError('manualScenario', 'Données de scénario manuel requises');
    return result;
  }

  // Validate name
  if (!manualScenario.name || typeof manualScenario.name !== 'string') {
    result.addError('name', 'Nom du scénario requis');
  } else {
    const sanitizedName = sanitizeString(manualScenario.name, { maxLength: 200 });
    if (sanitizedName.length < 3) {
      result.addError('name', 'Le nom doit contenir au moins 3 caractères');
    }
  }

  // Validate description
  if (!manualScenario.description || typeof manualScenario.description !== 'string') {
    result.addError('description', 'Description du scénario requise');
  } else {
    const sanitizedDescription = sanitizeString(manualScenario.description, { maxLength: 2000 });
    if (sanitizedDescription.length < 10) {
      result.addError('description', 'La description doit contenir au moins 10 caractères');
    }
  }

  // Validate severity if present
  if (manualScenario.severity) {
    const validSeverities = ['Faible', 'Modérée', 'Élevée', 'Critique'];
    if (!validSeverities.includes(manualScenario.severity)) {
      result.addError('severity', `Sévérité invalide. Valeurs acceptées: ${validSeverities.join(', ')}`);
    }
  }

  // Validate probability if present
  if (manualScenario.probability !== undefined && manualScenario.probability !== '') {
    const prob = parseFloat(manualScenario.probability);
    if (isNaN(prob) || prob < 0 || prob > 100) {
      result.addError('probability', 'La probabilité doit être un nombre entre 0 et 100');
    }
  }

  return result;
};

/**
 * Sanitize scenario data
 */
export const sanitizeScenario = (scenario) => {
  if (!scenario) return null;

  const sanitized = {
    ...scenario,
    name: sanitizeString(scenario.name, { maxLength: 200 }),
    description: sanitizeString(scenario.description, { maxLength: 2000 })
  };

  // Sanitize operational steps
  if (scenario.operationalSteps && Array.isArray(scenario.operationalSteps)) {
    sanitized.operationalSteps = scenario.operationalSteps.map(step => ({
      ...step,
      technique: sanitizeString(step.technique, { maxLength: 200 }),
      description: sanitizeString(step.description, { maxLength: 1000 }),
      // Sanitize indicators and tools arrays
      indicators: step.indicators ? step.indicators.map(indicator =>
        typeof indicator === 'string'
          ? sanitizeString(indicator, { maxLength: 100 })
          : {
            ...indicator,
            indicator: sanitizeString(indicator.indicator || indicator.name, { maxLength: 100 }),
            description: indicator.description ? sanitizeString(indicator.description, { maxLength: 500 }) : undefined
          }
      ) : undefined,
      tools: step.tools ? step.tools.map(tool =>
        typeof tool === 'string'
          ? sanitizeString(tool, { maxLength: 100 })
          : {
            ...tool,
            name: sanitizeString(tool.name || tool.tool, { maxLength: 100 }),
            description: tool.description ? sanitizeString(tool.description, { maxLength: 500 }) : undefined
          }
      ) : undefined
    }));
  }

  return sanitized;
};

/**
 * Validate and sanitize user input
 */
export const validateAndSanitize = (data, validationType) => {
  let validationResult;
  let sanitizedData = data;

  switch (validationType) {
    case 'scenario':
      sanitizedData = sanitizeScenario(data);
      validationResult = validateScenario(sanitizedData);
      break;
    case 'manualScenario':
      validationResult = validateManualScenario(data);
      if (validationResult.isValid) {
        sanitizedData = {
          ...data,
          name: sanitizeString(data.name, { maxLength: 200 }),
          description: sanitizeString(data.description, { maxLength: 2000 })
        };
      }
      break;
    case 'ctiData':
      validationResult = validateCTIData(data);
      sanitizedData = data; // CTI data is typically read-only
      break;
    case 'attackPathSelection':
      validationResult = validateAttackPathSelection(data.selectedAssets, data.availableAssets);
      sanitizedData = data;
      break;
    default:
      validationResult = new ValidationResult(false);
      validationResult.addError('validation', 'Type de validation non reconnu');
  }

  return {
    isValid: validationResult.isValid,
    errors: validationResult.errors,
    warnings: validationResult.warnings,
    sanitizedData
  };
};

export default {
  ValidationResult,
  sanitizeString,
  validateScenario,
  validateOperationalStep,
  validateCTIData,
  validateAttackPathSelection,
  validateManualScenario,
  sanitizeScenario,
  validateAndSanitize
};
